import { useMemo, useState } from 'react';
import { LAB_STEPS } from '../../content/lessons';
import { PRESETS } from '../../content/presets';
import { metricText } from '../../simulation/metrics';
import { describeController, FF_LABELS, type RunRecord } from '../../state/runRecord';
import { protectedRunIds, useApp } from '../../state/store';
import { parseMarkup } from '../Markup';
import { CapstoneCheck, MetricRows, runMetrics } from './MetricsPanel';

export function RunsPanel() {
  const runs = useApp((s) => s.runs);
  const comparison = useApp((s) => s.comparison);
  const evidence = useApp((s) => s.progress.evidence);
  const toggleCompare = useApp((s) => s.toggleCompare);
  const deleteRun = useApp((s) => s.deleteRun);
  const restoreRun = useApp((s) => s.restoreRun);
  const loadSavedRun = useApp((s) => s.loadSavedRun);
  const progress = useApp((s) => s.progress);
  const protectedIds = useMemo(() => protectedRunIds({ progress }), [progress]);
  const evidenceFor = useMemo(() => {
    const m = new Map<string, string>();
    for (const [step, id] of Object.entries(evidence)) m.set(id, LAB_STEPS[step]?.step.title ?? step);
    return m;
  }, [evidence]);
  const [open, setOpen] = useState<string | null>(null);
  const list = [...runs].reverse();

  if (!runs.length) return <p className="small muted">No saved runs yet. Completed runs are saved automatically.</p>;
  return (
    <div className="runs">
      <p className="small">
        Tick up to three runs to compare them with matching axes. {comparison.length}/3 selected.
      </p>
      <ul className="run-list">
        {list.map((r) => (
          <RunRow
            key={r.id}
            run={r}
            selected={comparison.includes(r.id)}
            evidenceTitle={evidenceFor.get(r.id) ?? null}
            protectedRun={protectedIds.has(r.id)}
            open={open === r.id}
            onToggleOpen={() => setOpen(open === r.id ? null : r.id)}
            onCompare={() => toggleCompare(r.id)}
            onDelete={() => deleteRun(r.id)}
            onRestore={() => restoreRun(r.id)}
            onLoad={() => loadSavedRun(r.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function RunRow(props: {
  run: RunRecord;
  selected: boolean;
  evidenceTitle: string | null;
  protectedRun: boolean;
  open: boolean;
  onToggleOpen: () => void;
  onCompare: () => void;
  onDelete: () => void;
  onRestore: () => void;
  onLoad: () => void;
}) {
  const { run: r, selected, evidenceTitle, protectedRun, open } = props;
  const m = useMemo(() => (open ? runMetrics(r) : null), [open, r]);
  const capstone = r.presetId.startsWith('capstone');
  return (
    <li className={`run-row${selected ? ' selected' : ''}`}>
      <div className="run-main">
        <label className="check">
          <input type="checkbox" checked={selected} onChange={props.onCompare} aria-label={`Compare run ${r.number}`} />
          <strong>Run {r.number}</strong>
        </label>
        <span className="run-desc">
          {PRESETS[r.presetId]?.title ?? r.presetId} · {describeController(r.config.controller)} · {FF_LABELS[r.config.controller.ffMode]}
          {r.config.noise.enabled ? ' · noise on' : ''}
        </span>
        <span className="badges">
          {r.status === 'stopped' && <span className="badge warn">stopped: {r.stopReason?.code}</span>}
          {evidenceTitle && <span className="badge ok">recorded: {parseMarkup(evidenceTitle)}</span>}
        </span>
      </div>
      <div className="row gap-s">
        <button type="button" className="btn small ghost" aria-expanded={open} onClick={props.onToggleOpen}>
          {open ? 'Hide details' : 'Details'}
        </button>
        <button type="button" className="btn small ghost" onClick={props.onLoad} title="Show this run’s plots for inspection">
          View plots
        </button>
        <button type="button" className="btn small ghost" onClick={props.onRestore}>
          Load settings
        </button>
        {!protectedRun && (
          <button type="button" className="btn small ghost danger" onClick={props.onDelete} aria-label={`Delete run ${r.number}`}>
            Delete
          </button>
        )}
      </div>
      {open && m && (
        <div className="run-details">
          <table className="kv">
            <tbody>
              <MetricRows m={m} stats={r.stats} kind={r.config.controller.kind} />
            </tbody>
          </table>
          {r.stopReason && <p className="small warn-text">{r.stopReason.message}</p>}
          {capstone && <CapstoneCheck record={r} />}
          <p className="hint">
            Recorded with {r.engine}; overshoot {metricText(m.overshoot, '')} is relative to the first target change.
          </p>
        </div>
      )}
    </li>
  );
}
